/**
 * Centralized retailer configuration system
 * This file contains all retailer-specific logic, configurations, and utilities
 */

import type { User } from "../services/UserService";
import type { CategoryV2 } from "../services/CategoriesService";

export type RetailerType =
  | "walmart"
  | "walmart_online"
  | "amazon"
  | "lowes"
  | "kohls"
  | "home_depot"
  | "target"
  | "target_online"
  | "dollar_general"
  | "cvs"
  | "woot"
  | "bestbuy"
  | "costco"
  | "tractor_supply"
  | "northern_tool"
  | "samsclub"
  | "ace_hardware"
  | "newegg"
  | "gamestop"
  | "bjs"
  | "meijer"
  | "menards"
  | "academy"
  | "walgreens"
  | "zappos"
  | "jcpenney"
  | "belk"
  | "michaels"
  | "nike"
  | "adidas"
  | "reebok"
  | "skechers"
  | "puma"
  | "underarmour"
  | "columbia"
  | "nordstromrack"
  | "aldo"
  | "vitacost"
  | "journeys"
  | "shopdisney"
  | "ninjakitchen"
  | "hottopic"
  | "officedepot"
  | "barnesnoble"
  | "razer"
  | "pacsun"
  | "bathbodyworks"
  | "staples"
  | "petsmart"
  | "ulta"
  | "forever21"
  | "gap"
  | "petco"
  | "americaneagle"
  | "aerie"
  | "carters"
  | "abercrombie"
  | "hollister"
  | "oldnavy"
  | "bananarepublic"
  | "stevemadden"
  | "athleta"
  | "gapfactory"
  | "bananarepublicfactory"
  | "boxlunch"
  | "verabradley"
  | "footlocker"
  | "champs";

// Base interface for retailer configuration
export interface RetailerConfig {
  id: RetailerType;
  name: string;
  displayName: string;
  features: {
    categories: boolean;
    searchTerms: boolean;
    stores: boolean;
    favorites: boolean;
    monitors: boolean;
    leads: boolean;
  };
  userProperties: {
    storesKey: keyof User;
    categoriesKey: keyof User;
    searchTermsKey: keyof User;
  };
  apiEndpoints: {
    categories: string;
    products: string;
    stores: string;
  };
  storeData: {
    storeIdField: string;
    addressField: string;
    displayFields: string[];
  };
  categoryService: {
    getCategories: () => Promise<CategoryV2[]>;
    hookName: string;
  };
  productService: {
    getProducts: (
      storeId: string,
      category?: string,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => Promise<any>;
    getNextPage: (
      storeId: string,
      cursor: string,
      category?: string,
      limit?: number,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => Promise<any>;
  };
  storeNormalization: {
    normalizeStoreId: (storeId: string) => string;
    formatStoreDisplay: (store: any) => string;
  };
}

// Walmart configuration
const walmartConfig: RetailerConfig = {
  id: "walmart",
  name: "Walmart",
  displayName: "Walmart",
  features: {
    categories: true,
    searchTerms: true,
    stores: true,
    favorites: true,
    leads: true,
    monitors: true,
  },
  userProperties: {
    storesKey: "storeIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categoriesV2",
    products: "/products",
    stores: "/stores",
  },
  storeData: {
    storeIdField: "store_id",
    addressField: "address",
    displayFields: ["address", "postal_code"],
  },
  categoryService: {
    getCategories: async () => {
      const { getCategoriesV2 } = await import("../services/CategoriesService");
      return getCategoriesV2();
    },
    hookName: "useCategories",
  },
  productService: {
    getProducts: async (
      storeId: string,
      category?: string,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getInitialProducts } = await import(
        "../services/ProductsService"
      );
      return getInitialProducts(
        storeId,
        category,
        25,
        showNewestOnly,
        retailer,
        showHotDeals
      );
    },
    getNextPage: async (
      storeId: string,
      cursor: string,
      category?: string,
      limit?: number,
      showNewestOnly?: boolean,
      retailer?: string
    ) => {
      const { getNextPage } = await import("../services/ProductsService");
      return getNextPage(
        storeId,
        cursor,
        category,
        limit || 25,
        showNewestOnly,
        retailer
      );
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId, // No normalization needed for Walmart
    formatStoreDisplay: (store: any) => `${store.address} ${store.postal_code}`,
  },
};

// Walmart Online configuration (no stores, uses online endpoint)
const walmartOnlineConfig: RetailerConfig = {
  id: "walmart_online",
  name: "Walmart Online",
  displayName: "Walmart Online",
  features: {
    categories: true,
    searchTerms: false,
    stores: false,
    favorites: true,
    leads: false,
    monitors: false,
  },
  userProperties: {
    storesKey: "storeIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categoriesV2",
    products: "/products-online",
    stores: "/stores",
  },
  storeData: {
    storeIdField: "store_id",
    addressField: "address",
    displayFields: ["address", "postal_code"],
  },
  categoryService: {
    getCategories: async () => {
      const { getCategoriesV2 } = await import("../services/CategoriesService");
      return getCategoriesV2();
    },
    hookName: "useCategories",
  },
  productService: {
    getProducts: async () => {
      return { products: [], nextCursor: null };
    },
    getNextPage: async () => {
      return { products: [], nextCursor: null };
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId,
    formatStoreDisplay: () => "Online",
  },
};

// Target Online configuration (no stores, uses online endpoint)
const targetOnlineConfig: RetailerConfig = {
  id: "target_online",
  name: "Target Online",
  displayName: "Target Online",
  features: {
    categories: true,
    searchTerms: false,
    stores: false,
    favorites: true,
    leads: false,
    monitors: false,
  },
  userProperties: {
    storesKey: "storeIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categoriesV2",
    products: "/products-online",
    stores: "/stores",
  },
  storeData: {
    storeIdField: "store_id",
    addressField: "address",
    displayFields: ["address", "postal_code"],
  },
  categoryService: {
    getCategories: async () => {
      const { getCategoriesV2 } = await import("../services/CategoriesService");
      return getCategoriesV2();
    },
    hookName: "useCategories",
  },
  productService: {
    getProducts: async () => {
      return { products: [], nextCursor: null };
    },
    getNextPage: async () => {
      return { products: [], nextCursor: null };
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId,
    formatStoreDisplay: () => "Online",
  },
};

// Amazon configuration (online-only, uses products-v2 with storeId=online)
const amazonConfig: RetailerConfig = {
  id: "amazon",
  name: "Amazon",
  displayName: "Amazon",
  features: {
    categories: true,
    searchTerms: false,
    stores: false,
    favorites: true,
    leads: false,
    monitors: false,
  },
  userProperties: {
    storesKey: "storeIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categoriesV2",
    products: "/products-v2",
    stores: "/stores",
  },
  storeData: {
    storeIdField: "store_id",
    addressField: "address",
    displayFields: ["address", "postal_code"],
  },
  categoryService: {
    getCategories: async () => {
      const { getCategoriesV2 } = await import("../services/CategoriesService");
      return getCategoriesV2();
    },
    hookName: "useCategories",
  },
  productService: {
    getProducts: async () => {
      return { products: [], nextCursor: null };
    },
    getNextPage: async () => {
      return { products: [], nextCursor: null };
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId,
    formatStoreDisplay: () => "Online",
  },
};

// Lowe's configuration (local stores plus the cached online feed)
const lowesConfig: RetailerConfig = {
  id: "lowes",
  name: "Lowes",
  displayName: "Lowe's",
  features: {
    categories: false,
    searchTerms: false,
    stores: true,
    favorites: false,
    leads: true,
    monitors: false,
  },
  userProperties: {
    storesKey: "lowesStoreIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categoriesV2",
    products: "/products-v2",
    stores: "/stores",
  },
  storeData: {
    storeIdField: "Store No.",
    addressField: "Street",
    displayFields: ["Name", "Street", "City", "State", "Zip_Code"],
  },
  categoryService: {
    getCategories: async () => [],
    hookName: "useCategories",
  },
  productService: {
    getProducts: async (
      storeId: string,
      category?: string,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getInitialProducts } = await import("../services/ProductsService");
      return getInitialProducts(storeId, category, 25, showNewestOnly, retailer, showHotDeals);
    },
    getNextPage: async (
      storeId: string,
      cursor: string,
      category?: string,
      limit?: number,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getNextPage } = await import("../services/ProductsService");
      return getNextPage(storeId, cursor, category, limit || 25, showNewestOnly, retailer, showHotDeals);
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId,
    formatStoreDisplay: (store: any) =>
      `#${store["Store No."] || store.store_id} - ${store.Name || store.name}\n${store.Street || store.street}, ${store.City || store.city}, ${store.State || store.state}`,
  },
};

const kohlsConfig: RetailerConfig = {
  id: "kohls",
  name: "Kohls",
  displayName: "Kohl's",
  features: {
    categories: true,
    searchTerms: false,
    stores: false,
    favorites: true,
    leads: false,
    monitors: false,
  },
  userProperties: {
    storesKey: "storeIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categoriesV2",
    products: "/products-v2",
    stores: "/stores",
  },
  storeData: {
    storeIdField: "store_id",
    addressField: "address",
    displayFields: ["address", "postal_code"],
  },
  categoryService: {
    getCategories: async () => {
      const { getCategoriesV2 } = await import("../services/CategoriesService");
      return getCategoriesV2();
    },
    hookName: "useCategories",
  },
  productService: {
    getProducts: async () => {
      return { products: [], nextCursor: null };
    },
    getNextPage: async () => {
      return { products: [], nextCursor: null };
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId,
    formatStoreDisplay: () => "Online",
  },
};

// Factory for online-only retailers that render via the generic /products-v2
// path (like Amazon). Product fetching is a no-op because the browse
// UI queries the backend directly.
function makeOnlineRetailerConfig(
  id: RetailerType,
  name: string,
  displayName: string
): RetailerConfig {
  return {
    id,
    name,
    displayName,
    features: {
      categories: true,
      searchTerms: false,
      stores: false,
      favorites: true,
      leads: false,
      monitors: false,
    },
    userProperties: {
      storesKey: "storeIds",
      categoriesKey: "categories",
      searchTermsKey: "searchTerms",
    },
    apiEndpoints: {
      categories: "/categoriesV2",
      products: "/products-v2",
      stores: "/stores",
    },
    storeData: {
      storeIdField: "store_id",
      addressField: "address",
      displayFields: ["address", "postal_code"],
    },
    categoryService: {
      getCategories: async () => {
        const { getCategoriesV2 } = await import("../services/CategoriesService");
        return getCategoriesV2();
      },
      hookName: "useCategories",
    },
    productService: {
      getProducts: async () => {
        return { products: [], nextCursor: null };
      },
      getNextPage: async () => {
        return { products: [], nextCursor: null };
      },
    },
    storeNormalization: {
      normalizeStoreId: (storeId: string) => storeId,
      formatStoreDisplay: () => "Online",
    },
  };
}

const neweggConfig = makeOnlineRetailerConfig("newegg", "Newegg", "Newegg");
const gamestopConfig = makeOnlineRetailerConfig("gamestop", "GameStop", "GameStop");
const bjsConfig = makeOnlineRetailerConfig("bjs", "BJs", "BJ's Wholesale");
const meijerConfig = makeOnlineRetailerConfig("meijer", "Meijer", "Meijer");
const menardsConfig = makeOnlineRetailerConfig("menards", "Menards", "Menards");
const academyConfig = makeOnlineRetailerConfig(
  "academy",
  "Academy",
  "Academy Sports + Outdoors"
);
const walgreensConfig = makeOnlineRetailerConfig(
  "walgreens",
  "Walgreens",
  "Walgreens"
);
const zapposConfig = makeOnlineRetailerConfig("zappos", "Zappos", "Zappos");
const jcpenneyConfig = makeOnlineRetailerConfig(
  "jcpenney",
  "JCPenney",
  "JCPenney"
);
const belkConfig = makeOnlineRetailerConfig("belk", "Belk", "Belk");
const michaelsConfig = makeOnlineRetailerConfig(
  "michaels",
  "Michaels",
  "Michaels"
);
const nikeConfig = makeOnlineRetailerConfig("nike", "Nike", "Nike");
const adidasConfig = makeOnlineRetailerConfig("adidas", "Adidas", "Adidas");
const reebokConfig = makeOnlineRetailerConfig("reebok", "Reebok", "Reebok");
const skechersConfig = makeOnlineRetailerConfig(
  "skechers",
  "Skechers",
  "Skechers"
);
const pumaConfig = makeOnlineRetailerConfig("puma", "Puma", "Puma");
const underarmourConfig = makeOnlineRetailerConfig(
  "underarmour",
  "Under Armour",
  "Under Armour"
);
const columbiaConfig = makeOnlineRetailerConfig(
  "columbia",
  "Columbia",
  "Columbia"
);
const nordstromrackConfig = makeOnlineRetailerConfig(
  "nordstromrack",
  "Nordstrom Rack",
  "Nordstrom Rack"
);
const aldoConfig = makeOnlineRetailerConfig("aldo", "Aldo", "Aldo");
const vitacostConfig = makeOnlineRetailerConfig("vitacost", "Vitacost", "Vitacost");
const journeysConfig = makeOnlineRetailerConfig("journeys", "Journeys", "Journeys");
const shopdisneyConfig = makeOnlineRetailerConfig("shopdisney", "shopDisney", "shopDisney");
const ninjakitchenConfig = makeOnlineRetailerConfig("ninjakitchen", "Ninja Kitchen", "Ninja Kitchen");
const hottopicConfig = makeOnlineRetailerConfig("hottopic", "Hot Topic", "Hot Topic");
const officedepotConfig = makeOnlineRetailerConfig("officedepot", "Office Depot", "Office Depot");
const barnesnobleConfig = makeOnlineRetailerConfig("barnesnoble", "Barnes & Noble", "Barnes & Noble");
const razerConfig = makeOnlineRetailerConfig("razer", "Razer", "Razer");
const pacsunConfig = makeOnlineRetailerConfig("pacsun", "PacSun", "PacSun");
const bathbodyworksConfig = makeOnlineRetailerConfig("bathbodyworks", "Bath & Body Works", "Bath & Body Works");
const staplesConfig = makeOnlineRetailerConfig("staples", "Staples", "Staples");
const petsmartConfig = makeOnlineRetailerConfig("petsmart", "PetSmart", "PetSmart");
const ultaConfig = makeOnlineRetailerConfig("ulta", "Ulta Beauty", "Ulta Beauty");
const forever21Config = makeOnlineRetailerConfig("forever21", "Forever 21", "Forever 21");
const gapConfig = makeOnlineRetailerConfig("gap", "Gap", "Gap");
const petcoConfig = makeOnlineRetailerConfig("petco", "Petco", "Petco");
const americaneagleConfig = makeOnlineRetailerConfig("americaneagle", "American Eagle", "American Eagle");
const aerieConfig = makeOnlineRetailerConfig("aerie", "Aerie", "Aerie");
const cartersConfig = makeOnlineRetailerConfig("carters", "Carter's", "Carter's");
const abercrombieConfig = makeOnlineRetailerConfig("abercrombie", "Abercrombie & Fitch", "Abercrombie & Fitch");
const hollisterConfig = makeOnlineRetailerConfig("hollister", "Hollister", "Hollister");
const oldnavyConfig = makeOnlineRetailerConfig("oldnavy", "Old Navy", "Old Navy");
const bananarepublicConfig = makeOnlineRetailerConfig("bananarepublic", "Banana Republic", "Banana Republic");
const stevemaddenConfig = makeOnlineRetailerConfig("stevemadden", "Steve Madden", "Steve Madden");
const athletaConfig = makeOnlineRetailerConfig("athleta", "Athleta", "Athleta");
const gapfactoryConfig = makeOnlineRetailerConfig("gapfactory", "Gap Factory", "Gap Factory");
const bananarepublicfactoryConfig = makeOnlineRetailerConfig("bananarepublicfactory", "Banana Republic Factory", "Banana Republic Factory");
const boxlunchConfig = makeOnlineRetailerConfig("boxlunch", "BoxLunch", "BoxLunch");
const verabradleyConfig = makeOnlineRetailerConfig("verabradley", "Vera Bradley", "Vera Bradley");
const footlockerConfig = makeOnlineRetailerConfig("footlocker", "Foot Locker", "Foot Locker");
const champsConfig = makeOnlineRetailerConfig("champs", "Champs Sports", "Champs Sports");

const dollarGeneralConfig: RetailerConfig = {
  id: "dollar_general",
  name: "Dollar General",
  displayName: "Dollar General",
  features: {
    categories: true,
    searchTerms: false,
    stores: true,
    favorites: false,
    leads: true,
    monitors: false,
  },
  userProperties: {
    storesKey: "dollarGeneralStoreIds",
    categoriesKey: "dollarGeneralCategories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categories-dollar-general",
    products: "/products-dollar-general",
    stores: "/stores-dollar-general",
  },
  storeData: {
    storeIdField: "storeId",
    addressField: "address",
    displayFields: ["address", "postal_code"],
  },
  categoryService: {
    getCategories: async () => {
      const { getDollarGeneralCategories } = await import(
        "../services/CategoriesService"
      );
      return getDollarGeneralCategories();
    },
    hookName: "useDollarGeneralCategories",
  },
  productService: {
    getProducts: async (
      storeId: string,
      category?: string,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getInitialProducts } = await import(
        "../services/ProductsService"
      );
      return getInitialProducts(
        storeId,
        category,
        25,
        showNewestOnly,
        retailer,
        showHotDeals
      );
    },
    getNextPage: async (
      storeId: string,
      cursor: string,
      category?: string,
      limit?: number,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getNextPage } = await import("../services/ProductsService");
      return getNextPage(
        storeId,
        cursor,
        category,
        limit || 25,
        showNewestOnly,
        retailer,
        showHotDeals
      );
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => {
      // Pad Home Depot store numbers with leading zero if they are 3 digits
      if (/^\d{3}$/.test(storeId)) {
        return storeId.padStart(4, "0");
      }
      return storeId;
    },
    formatStoreDisplay: (store: any) =>
      `#${store.store_id} - ${store.location_name}\n${store.mailing_address.address_line1}`,
  },
};

// CVS never asks members to manage stores. The API derives the nearest three
// full CVS Pharmacy locations from the ZIP code saved on the account.
const cvsConfig: RetailerConfig = {
  id: "cvs",
  name: "CVS",
  displayName: "CVS",
  features: {
    categories: false,
    searchTerms: false,
    stores: false,
    favorites: true,
    leads: false,
    monitors: false,
  },
  userProperties: {
    storesKey: "cvsStoreIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categoriesV2",
    products: "/products-v2",
    stores: "/stores",
  },
  storeData: {
    storeIdField: "storeId",
    addressField: "address",
    displayFields: ["address", "postal_code"],
  },
  categoryService: {
    getCategories: async () => [],
    hookName: "useCategories",
  },
  productService: {
    getProducts: async (
      storeId,
      category,
      showNewestOnly,
      retailer,
      showHotDeals
    ) => {
      const { getInitialProducts } = await import("../services/ProductsService");
      return getInitialProducts(
        storeId,
        category,
        25,
        showNewestOnly,
        retailer,
        showHotDeals
      );
    },
    getNextPage: async (
      storeId,
      cursor,
      category,
      limit,
      showNewestOnly,
      retailer,
      showHotDeals
    ) => {
      const { getNextPage } = await import("../services/ProductsService");
      return getNextPage(
        storeId,
        cursor,
        category,
        limit || 25,
        showNewestOnly,
        retailer,
        showHotDeals
      );
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId,
    formatStoreDisplay: (store: any) =>
      store?.address || store?.name || `CVS #${store?.storeId || ""}`,
  },
};

// Target configuration
const targetConfig: RetailerConfig = {
  id: "target",
  name: "Target",
  displayName: "Target",
  features: {
    categories: false,
    searchTerms: false,
    stores: true,
    favorites: false,
    monitors: false,
    leads: true,
  },
  userProperties: {
    storesKey: "targetStoreIds",
    categoriesKey: "targetCategories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categories-target",
    products: "/products-target",
    stores: "/stores-target",
  },
  storeData: {
    storeIdField: "store_id",
    addressField: "location_name",
    displayFields: ["address_1", "city", "state", "zip"],
  },
  categoryService: {
    getCategories: async () => {
      const { getTargetCategories } = await import(
        "../services/CategoriesService"
      );
      return getTargetCategories();
    },
    hookName: "useTargetCategories",
  },
  productService: {
    getProducts: async (
      storeId: string,
      category?: string,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getInitialProducts } = await import(
        "../services/ProductsService"
      );
      return getInitialProducts(
        storeId,
        category,
        25,
        showNewestOnly,
        retailer,
        showHotDeals
      );
    },
    getNextPage: async (
      storeId: string,
      cursor: string,
      category?: string,
      limit?: number,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getNextPage } = await import("../services/ProductsService");
      return getNextPage(
        storeId,
        cursor,
        category,
        limit || 25,
        showNewestOnly,
        retailer,
        showHotDeals
      );
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => {
      // Pad Home Depot store numbers with leading zero if they are 3 digits
      if (/^\d{3}$/.test(storeId)) {
        return storeId.padStart(4, "0");
      }
      return storeId;
    },
    formatStoreDisplay: (store: any) =>
      `#${store.store_id} - ${store.location_name}\n${store.mailing_address.address_line1}`,
  },
};
// Home Depot configuration
const homeDepotConfig: RetailerConfig = {
  id: "home_depot",
  name: "HD",
  displayName: "HD",
  features: {
    categories: true,
    searchTerms: false, // Home Depot doesn't support search terms
    stores: true,
    favorites: true,
    leads: true,
    monitors: true,
  },
  userProperties: {
    storesKey: "homeDepotStoreIds",
    categoriesKey: "homeDepotCategories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categories-home-depot",
    products: "/products-home-depot",
    stores: "/stores-home_depot",
  },
  storeData: {
    storeIdField: "store_number",
    addressField: "address_1",
    displayFields: ["address_1", "city", "state", "zip"],
  },
  categoryService: {
    getCategories: async () => {
      const { getHomeDepotCategories } = await import(
        "../services/CategoriesService"
      );
      return getHomeDepotCategories();
    },
    hookName: "useHomeDepotCategories",
  },
  productService: {
    getProducts: async (
      storeId: string,
      category?: string,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getInitialProducts } = await import(
        "../services/ProductsService"
      );
      return getInitialProducts(
        storeId,
        category,
        25,
        showNewestOnly,
        retailer,
        showHotDeals
      );
    },
    getNextPage: async (
      storeId: string,
      cursor: string,
      category?: string,
      limit?: number,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getNextPage } = await import("../services/ProductsService");
      return getNextPage(
        storeId,
        cursor,
        category,
        limit || 25,
        showNewestOnly,
        retailer,
        showHotDeals
      );
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => {
      // Pad Home Depot store numbers with leading zero if they are 3 digits
      if (/^\d{3}$/.test(storeId)) {
        return storeId.padStart(4, "0");
      }
      return storeId;
    },
    formatStoreDisplay: (store: any) =>
      `#${store.store_number} - ${store.store_title}\n${store.address_1}, ${store.city}, ${store.state} ${store.zip}`,
  },
};

const wootConfig: RetailerConfig = {
  id: "woot",
  name: "Woot",
  displayName: "Woot",
  features: {
    categories: false,
    searchTerms: false,
    stores: false,
    favorites: true,
    leads: false,
    monitors: false,
  },
  userProperties: {
    storesKey: "storeIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categories-woot",
    products: "/products-woot",
    stores: "/stores-woot",
  },
  storeData: {
    storeIdField: "storeId",
    addressField: "address",
    displayFields: ["address"],
  },
  categoryService: {
    getCategories: async () => {
      return [];
    },
    hookName: "useWootCategories",
  },
  productService: {
    getProducts: async () => {
      return [];
    },
    getNextPage: async () => {
      return [];
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId,
    formatStoreDisplay: (store: any) => store.address || "",
  },
};

// Best Buy configuration (users select up to 5 stores)
const bestbuyConfig: RetailerConfig = {
  id: "bestbuy",
  name: "Best Buy",
  displayName: "Best Buy",
  features: {
    categories: false, // Categories are dynamic from products
    searchTerms: false,
    stores: true, // Users select stores from list
    favorites: false,
    leads: true,
    monitors: false,
  },
  userProperties: {
    storesKey: "bestbuyStoreIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categories-bestbuy",
    products: "/products-v2",
    stores: "/stores-bestbuy",
  },
  storeData: {
    storeIdField: "store_number",
    addressField: "street",
    displayFields: ["name", "street", "city", "state", "zip"],
  },
  categoryService: {
    getCategories: async () => {
      return [];
    },
    hookName: "useBestBuyCategories",
  },
  productService: {
    getProducts: async (
      storeId: string,
      category?: string,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getInitialProducts } = await import("../services/ProductsService");
      return getInitialProducts(storeId, category, 25, showNewestOnly, retailer, showHotDeals);
    },
    getNextPage: async (
      storeId: string,
      cursor: string,
      category?: string,
      limit?: number,
      showNewestOnly?: boolean,
      retailer?: string,
      showHotDeals?: boolean
    ) => {
      const { getNextPage } = await import("../services/ProductsService");
      return getNextPage(storeId, cursor, category, limit || 25, showNewestOnly, retailer, showHotDeals);
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId,
    formatStoreDisplay: (store: any) =>
      `#${store.store_number} - ${store.name}\n${store.street}, ${store.city}, ${store.state} ${store.zip}`,
  },
};

// Costco configuration (users select up to 3 warehouses)
const costcoConfig: RetailerConfig = {
  id: "costco",
  name: "Costco",
  displayName: "Costco",
  features: {
    categories: false,
    searchTerms: false,
    stores: true,
    favorites: false,
    leads: true,
    monitors: false,
  },
  userProperties: {
    storesKey: "costcoStoreIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categories-costco",
    products: "/products-v2",
    stores: "/stores-costco",
  },
  storeData: {
    storeIdField: "warehouse_number",
    addressField: "warehouse_name",
    displayFields: ["warehouse_name", "address", "location"],
  },
  categoryService: {
    getCategories: async () => [],
    hookName: "useCostcoCategories",
  },
  productService: {
    getProducts: async (storeId, category, showNewestOnly, retailer, showHotDeals) => {
      const { getInitialProducts } = await import("../services/ProductsService");
      return getInitialProducts(storeId, category, 25, showNewestOnly, retailer, showHotDeals);
    },
    getNextPage: async (storeId, cursor, category, limit, showNewestOnly, retailer, showHotDeals) => {
      const { getNextPage } = await import("../services/ProductsService");
      return getNextPage(storeId, cursor, category, limit || 25, showNewestOnly, retailer, showHotDeals);
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId,
    formatStoreDisplay: (store: any) =>
      `${store.warehouse_name || store.warehouse_number}\n${store.address || ""}, ${store.location || ""}`,
  },
};

const tractorSupplyConfig: RetailerConfig = {
  id: "tractor_supply",
  name: "Tractor Supply",
  displayName: "Tractor Supply",
  features: {
    categories: false,
    searchTerms: false,
    stores: true,
    favorites: true,
    leads: true,
    monitors: false,
  },
  userProperties: {
    storesKey: "tractorSupplyStoreIds",
    categoriesKey: "categories",
    searchTermsKey: "searchTerms",
  },
  apiEndpoints: {
    categories: "/categories",
    products: "/products-tractor-supply",
    stores: "/stores-tractor-supply",
  },
  storeData: {
    storeIdField: "store_number",
    addressField: "street",
    displayFields: ["label", "street", "city", "state", "zip"],
  },
  categoryService: {
    getCategories: async () => [],
    hookName: "useTractorSupplyCategories",
  },
  productService: {
    getProducts: async (storeId, category, showNewestOnly, retailer, showHotDeals) => {
      const { getInitialProducts } = await import("../services/ProductsService");
      return getInitialProducts(storeId, category, 25, showNewestOnly, retailer, showHotDeals);
    },
    getNextPage: async (storeId, cursor, category, limit, showNewestOnly, retailer, showHotDeals) => {
      const { getNextPage } = await import("../services/ProductsService");
      return getNextPage(storeId, cursor, category, limit || 25, showNewestOnly, retailer, showHotDeals);
    },
  },
  storeNormalization: {
    normalizeStoreId: (storeId: string) => storeId.replace(/^0+/, "") || storeId,
    formatStoreDisplay: (store: any) =>
      `${store.label || `${store.city || "Store"} - #${store.store_number}`}\n${store.street || ""}, ${store.city || ""}, ${store.state || ""} ${store.zip || ""}`,
  },
};

function makeLocalClearanceRetailerConfig(
  id: "northern_tool" | "samsclub",
  displayName: string,
  storesKey: "northernToolStoreIds" | "samsClubStoreIds"
): RetailerConfig {
  return {
    ...tractorSupplyConfig,
    id,
    name: displayName,
    displayName,
    features: { ...tractorSupplyConfig.features, leads: false },
    userProperties: { ...tractorSupplyConfig.userProperties, storesKey },
    apiEndpoints: {
      categories: "/categories",
      products: "/products-v2",
      stores: `/stores-${id.replace(/_/g, "-")}`,
    },
    storeNormalization: {
      ...tractorSupplyConfig.storeNormalization,
      normalizeStoreId: (storeId: string) => storeId.replace(/^0+/, "") || storeId,
    },
  };
}

const northernToolConfig = makeLocalClearanceRetailerConfig(
  "northern_tool",
  "Northern Tool",
  "northernToolStoreIds"
);
// Sam's Club: in-store clearance / instant savings from the generic products-v2
// feed. Its catalog is aliased onto the local-clearance shape by storesLoader.
const samsClubConfig = makeLocalClearanceRetailerConfig(
  "samsclub",
  "Sam's Club",
  "samsClubStoreIds"
);
const aceHardwareConfig = makeOnlineRetailerConfig(
  "ace_hardware",
  "Ace Hardware",
  "Ace Hardware"
);

// Retailer configuration registry
export const RETAILER_CONFIGS: Record<RetailerType, RetailerConfig> = {
  walmart: walmartConfig,
  walmart_online: walmartOnlineConfig,
  amazon: amazonConfig,
  lowes: lowesConfig,
  kohls: kohlsConfig,
  home_depot: homeDepotConfig,
  target: targetConfig,
  target_online: targetOnlineConfig,
  dollar_general: dollarGeneralConfig,
  cvs: cvsConfig,
  woot: wootConfig,
  bestbuy: bestbuyConfig,
  costco: costcoConfig,
  tractor_supply: tractorSupplyConfig,
  northern_tool: northernToolConfig,
  samsclub: samsClubConfig,
  ace_hardware: aceHardwareConfig,
  newegg: neweggConfig,
  gamestop: gamestopConfig,
  bjs: bjsConfig,
  meijer: meijerConfig,
  menards: menardsConfig,
  academy: academyConfig,
  walgreens: walgreensConfig,
  zappos: zapposConfig,
  jcpenney: jcpenneyConfig,
  belk: belkConfig,
  michaels: michaelsConfig,
  nike: nikeConfig,
  adidas: adidasConfig,
  reebok: reebokConfig,
  skechers: skechersConfig,
  puma: pumaConfig,
  underarmour: underarmourConfig,
  columbia: columbiaConfig,
  nordstromrack: nordstromrackConfig,
  aldo: aldoConfig,
  vitacost: vitacostConfig,
  journeys: journeysConfig,
  shopdisney: shopdisneyConfig,
  ninjakitchen: ninjakitchenConfig,
  hottopic: hottopicConfig,
  officedepot: officedepotConfig,
  barnesnoble: barnesnobleConfig,
  razer: razerConfig,
  pacsun: pacsunConfig,
  bathbodyworks: bathbodyworksConfig,
  staples: staplesConfig,
  petsmart: petsmartConfig,
  ulta: ultaConfig,
  forever21: forever21Config,
  gap: gapConfig,
  petco: petcoConfig,
  americaneagle: americaneagleConfig,
  aerie: aerieConfig,
  carters: cartersConfig,
  abercrombie: abercrombieConfig,
  hollister: hollisterConfig,
  oldnavy: oldnavyConfig,
  bananarepublic: bananarepublicConfig,
  stevemadden: stevemaddenConfig,
  athleta: athletaConfig,
  gapfactory: gapfactoryConfig,
  bananarepublicfactory: bananarepublicfactoryConfig,
  boxlunch: boxlunchConfig,
  verabradley: verabradleyConfig,
  footlocker: footlockerConfig,
  champs: champsConfig,
};

/** Online-only retailers that use the generic products-v2 feed. */
export const EXTRA_ONLINE_RETAILERS: RetailerType[] = [
  "ace_hardware", "newegg", "gamestop", "bjs", "meijer", "menards", "academy", "walgreens", "zappos",
  "jcpenney", "belk", "michaels", "nike", "adidas", "reebok", "skechers",
  "puma", "underarmour", "columbia", "nordstromrack", "aldo",
  "vitacost", "journeys", "shopdisney", "ninjakitchen", "hottopic", "officedepot",
  "barnesnoble", "razer", "pacsun", "bathbodyworks", "staples", "petsmart", "ulta",
  "forever21", "gap", "petco", "americaneagle", "aerie", "carters", "abercrombie", "hollister",
  "oldnavy", "bananarepublic", "stevemadden", "athleta",
  "gapfactory", "bananarepublicfactory", "boxlunch", "verabradley", "footlocker", "champs",
];

export const ONLINE_ONLY_RETAILERS: RetailerType[] = [
  "walmart_online", "target_online", "amazon", "kohls", "woot",
  ...EXTRA_ONLINE_RETAILERS,
];

/** Retailer brands that expose both the free online feed and nearby store inventory. */
export const RETAILERS_WITH_ONLINE_AND_NEARBY: RetailerType[] = [
  "walmart",
  "home_depot",
  "lowes",
  "tractor_supply",
  "northern_tool",
  "target",
  "bestbuy",
  "costco",
];

/**
 * Return the physical-store retailer behind a dual-mode feed. Online aliases
 * normalize back to their store-capable brand so switching to Nearby works.
 */
export function getOnlineAndNearbyRetailerBase(
  retailer: string | null | undefined
): RetailerType | null {
  const normalized = retailer?.toLowerCase().trim();
  const base = normalized === "walmart_online"
    ? "walmart"
    : normalized === "target_online"
      ? "target"
      : normalized;
  return RETAILERS_WITH_ONLINE_AND_NEARBY.includes(base as RetailerType)
    ? (base as RetailerType)
    : null;
}

export function supportsOnlineAndNearbyModes(
  retailer: string | null | undefined
): boolean {
  return getOnlineAndNearbyRetailerBase(retailer) !== null;
}

/** Retailers whose deal feed offers the Crazy deals toggle (crawler-written
 * "<store>-CRAZY-DEALS" partition). Home Depot and Dollar General get the Penny
 * deals pill instead; see PENNY_ITEMS_RETAILERS. */
export const CRAZY_DEALS_RETAILERS: RetailerType[] = ["walmart"];

export function supportsCrazyDeals(retailer: string | null | undefined): boolean {
  if (!retailer) return false;
  return CRAZY_DEALS_RETAILERS.includes(retailer.toLowerCase().trim() as RetailerType);
}

/** Retailers whose deal feed shows the Penny deals pill: Home Depot opens the
 * Penny Items Pro screen, Dollar General toggles its "<store>-PENNY-ITEM" list. */
export const PENNY_ITEMS_RETAILERS: RetailerType[] = ["home_depot", "dollar_general"];

export function supportsPennyItems(retailer: string | null | undefined): boolean {
  if (!retailer) return false;
  return PENNY_ITEMS_RETAILERS.includes(retailer.toLowerCase().trim() as RetailerType);
}

/**
 * In-store feeds whose retailer is mapped in the Ibotta offers snapshot, so their
 * products can carry "Possible Ibotta offer" annotations and the Ibotta status filter.
 */
export const IBOTTA_RETAILERS: RetailerType[] = ["walmart", "dollar_general", "target", "costco", "home_depot"];

export function supportsIbottaOffers(retailer: string | null | undefined): boolean {
  return !!retailer && (IBOTTA_RETAILERS as string[]).includes(retailer);
}

export const BONUS_RETAILERS: RetailerType[] = [
  "dollar_general", "cvs", "target", "target_online", "bestbuy", "costco", "amazon",
  "lowes", "tractor_supply", "northern_tool", "samsclub", "kohls", "woot", "walmart_online", ...EXTRA_ONLINE_RETAILERS,
];

// Keep inactive configurations for historical products, but hide their feeds
// from client navigation and URLs.
const PAUSED_CLIENT_RETAILERS = new Set<RetailerType>(["cvs", "carters"]);

export function isRetailerClientEnabled(retailer: string | null | undefined): boolean {
  if (!retailer) return false;
  const normalized = retailer.toLowerCase().trim();
  return Object.prototype.hasOwnProperty.call(RETAILER_CONFIGS, normalized) &&
    !PAUSED_CLIENT_RETAILERS.has(normalized as RetailerType);
}

export function isExtraOnlineRetailer(retailer: string | null | undefined): boolean {
  return !!retailer && (EXTRA_ONLINE_RETAILERS as string[]).includes(retailer);
}

export function isOnlineOnlyRetailer(retailer: string | null | undefined): boolean {
  return !!retailer && (ONLINE_ONLY_RETAILERS as string[]).includes(retailer);
}

export function isBonusRetailer(retailer: string | null | undefined): boolean {
  return !!retailer && (BONUS_RETAILERS as string[]).includes(retailer);
}

// Utility functions
export function getRetailerConfig(retailer: RetailerType): RetailerConfig {
  return RETAILER_CONFIGS[retailer] || RETAILER_CONFIGS.walmart;
}

export function getAllRetailers(): RetailerType[] {
  return (Object.keys(RETAILER_CONFIGS) as RetailerType[]).filter(
    isRetailerClientEnabled
  );
}

export function getRetailerDisplayName(retailer: RetailerType): string {
  return getRetailerConfig(retailer).displayName;
}

export function isRetailerFeatureEnabled(
  retailer: RetailerType,
  feature: keyof RetailerConfig["features"]
): boolean {
  return getRetailerConfig(retailer).features[feature];
}

/** Normalize store id arrays from the user API (matches server user.GetStoreIds fields). */
export function normalizeUserStoreIds(stores: unknown): string[] {
  if (!Array.isArray(stores)) return [];
  return stores
    .map((store) => (store == null ? "" : String(store).trim()))
    .filter((store) => store.length > 0);
}

/** Read store ids using the same json fields as server/pkg/user.User. */
export function getUserStoreIdsField(
  user: User,
  retailer: RetailerType
): unknown {
  switch (retailer) {
    case "home_depot":
      return user.homeDepotStoreIds;
    case "target":
    case "target_online":
      return user.targetStoreIds;
    case "dollar_general":
      return user.dollarGeneralStoreIds;
    case "cvs":
      return [];
    case "bestbuy":
      return user.bestbuyStoreIds;
    case "costco":
      return user.costcoStoreIds;
    case "lowes":
      return user.lowesStoreIds;
    case "tractor_supply":
      return user.tractorSupplyStoreIds;
    case "northern_tool":
      return user.northernToolStoreIds;
    case "samsclub":
      return user.samsClubStoreIds;
    case "walmart":
    case "walmart_online":
    case "amazon":
    case "ace_hardware":
    case "kohls":
    case "woot":
    case "newegg":
    case "gamestop":
    case "bjs":
    case "meijer":
    case "menards":
    case "academy":
    case "walgreens":
    case "zappos":
    case "jcpenney":
    case "belk":
    case "michaels":
    case "nike":
    case "adidas":
    case "reebok":
    case "skechers":
    case "puma":
    case "underarmour":
    case "columbia":
    case "nordstromrack":
    case "aldo":
    case "vitacost":
    case "journeys":
    case "shopdisney":
    case "ninjakitchen":
    case "hottopic":
    case "officedepot":
    case "barnesnoble":
    case "razer":
    case "pacsun":
    case "bathbodyworks":
    case "staples":
    default:
      return user.storeIds;
  }
}

export function getUserStoresForRetailer(
  user: User | null,
  retailer: RetailerType
): string[] {
  if (!user) return [];
  return normalizeUserStoreIds(getUserStoreIdsField(user, retailer));
}

export function getUserCategoriesForRetailer(
  user: User | null,
  retailer: RetailerType
): string[] {
  if (!user) return [];

  const config = getRetailerConfig(retailer);
  const categories = user[config.userProperties.categoriesKey];

  if (Array.isArray(categories)) {
    return categories.filter(
      (category): category is string => typeof category === "string"
    );
  }

  return [];
}

export function getUserSearchTermsForRetailer(
  user: User | null,
  retailer: RetailerType
): string[] {
  if (!user) return [];
  const config = getRetailerConfig(retailer);
  const terms = user[config.userProperties.searchTermsKey];
  if (Array.isArray(terms)) {
    return terms.filter((t): t is string => typeof t === "string");
  }
  return [];
}

export function normalizeStoreId(
  storeId: string,
  retailer: RetailerType
): string {
  const config = getRetailerConfig(retailer);
  return config.storeNormalization.normalizeStoreId(storeId);
}

export function formatStoreDisplay(store: any, retailer: RetailerType): string {
  const config = getRetailerConfig(retailer);
  return config.storeNormalization.formatStoreDisplay(store);
}

export function getStoreIdField(retailer: RetailerType): string {
  const config = getRetailerConfig(retailer);
  return config.storeData.storeIdField;
}

export function getAddressField(retailer: RetailerType): string {
  const config = getRetailerConfig(retailer);
  return config.storeData.addressField;
}

export function getDisplayFields(retailer: RetailerType): string[] {
  const config = getRetailerConfig(retailer);
  return config.storeData.displayFields;
}

export function getCategoryServiceHook(retailer: RetailerType): string {
  const config = getRetailerConfig(retailer);
  return config.categoryService.hookName;
}

export function getCategoriesEndpoint(retailer: RetailerType): string {
  const config = getRetailerConfig(retailer);
  return config.apiEndpoints.categories;
}

export function getProductsEndpoint(retailer: RetailerType): string {
  const config = getRetailerConfig(retailer);
  return config.apiEndpoints.products;
}

export function getStoresEndpoint(retailer: RetailerType): string {
  const config = getRetailerConfig(retailer);
  return config.apiEndpoints.stores;
}

// Default store service integration
export function getDefaultStoreKey(retailer: RetailerType): string {
  return `dealhawk_default_store_${retailer}`;
}

// Query key generation for React Query
export function getProductsQueryKey(
  storeId: string,
  category?: string,
  showNewestOnly?: boolean,
  retailer?: string,
  showHotDeals?: boolean
): string[] {
  const retailerKey = retailer || "walmart";
  return [
    "products",
    retailerKey,
    storeId,
    category || "all",
    showNewestOnly ? "newest" : "all",
    showHotDeals ? "crazy-deals" : "all",
  ];
}

export function getCategoriesQueryKey(retailer: RetailerType): string[] {
  return ["categories", retailer];
}

export function getStoresQueryKey(retailer: RetailerType): string[] {
  return ["stores", retailer];
}

// Store cache key generation
export function getStoreCacheKey(
  retailer: RetailerType,
  storeId: string,
  category?: string,
  showNewestOnly?: boolean,
  showHotDeals?: boolean
): string {
  return `${retailer}-${storeId}-${category || "all"}-${
    showNewestOnly ? "newest" : "all"
  }-${showHotDeals ? "crazy-deals" : "all"}`;
}

// Retailer-specific validation
export function validateRetailer(retailer: string): retailer is RetailerType {
  return retailer in RETAILER_CONFIGS && isRetailerClientEnabled(retailer);
}

// Get retailer from URL params with fallback
export function getRetailerFromParams(retailerParam?: string): RetailerType {
  if (retailerParam && validateRetailer(retailerParam)) {
    return retailerParam;
  }
  return "walmart";
}

// Retailer-specific user update data preparation
export function prepareUserUpdateData(
  retailer: RetailerType,
  updateData: {
    categories?: string[];
    stores?: string[];
    searchTerms?: string[];
  }
): Partial<User> {
  const config = getRetailerConfig(retailer);
  const result: Partial<User> = {};

  if (updateData.categories !== undefined) {
    (result as any)[config.userProperties.categoriesKey] =
      updateData.categories;
  }

  if (updateData.stores !== undefined) {
    // Format store IDs based on retailer
    let formattedStores = updateData.stores;

    // For Home Depot, remove leading zeros from store IDs
    if (retailer === "home_depot") {
      formattedStores = [...new Set(updateData.stores
        .map((storeId) => storeId.trim())
        .filter(Boolean)
        .map((storeId) => storeId.replace(/^0+/, "") || storeId))];
    }

    (result as any)[config.userProperties.storesKey] = formattedStores;
  }

  if (updateData.searchTerms !== undefined) {
    (result as any)[config.userProperties.searchTermsKey] = updateData.searchTerms;
  }

  return result;
}

// Retailer-specific feature availability
export function getAvailableTabs(retailer: RetailerType): Array<{
  id: string;
  name: string;
  shortName: string;
  enabled: boolean;
}> {
  const config = getRetailerConfig(retailer);

  return [
    {
      id: "categories",
      name: "Categories",
      shortName: "Categories",
      enabled: config.features.categories,
    },
    {
      id: "searchTerms",
      name: "Search Terms",
      shortName: "Terms",
      enabled: config.features.searchTerms,
    },
    {
      id: "stores",
      name: "Stores",
      shortName: "Stores",
      enabled: config.features.stores,
    },
    {
      id: "favorites",
      name: "Saved",
      shortName: "Saved",
      enabled: config.features.favorites,
    },
    {
      id: "monitors",
      name: "Monitors",
      shortName: "Monitors",
      enabled: config.features.monitors,
    },
  ].filter((tab) => tab.enabled);
}
